import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import placeholder from '@/shared/assets/images/vodPlaceholder.webp'
import { ProgressBar } from '@/features'
import { iconStyle } from '@/features/VOD/components/itemDetails/styles/detailsButtonsStyles'
import { BuyButton } from '@/shared/ui'
import { EpisodeItemInterface } from '@/types/vod/VodItemDetails'
import { isSmartTV, parseAspectRatioToNumber } from '@/shared/lib'
import { episodeItemHeight } from '@/shared/constants'
import {
    FocusableComponentLayout,
    FocusDetails,
    useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import { ReactComponent as PlayIcon } from '@/shared/assets/icons/playIcon.svg'

interface SeriesEpisodeSliderItemProps {
    episode: EpisodeItemInterface
    handleBuyEpisode: (episode: EpisodeItemInterface) => void
    progressBarWrapper: object
    notPurchased: object
    buyButtonStyle: object
    handleMouseEnter?: (episode: EpisodeItemInterface) => void
    onFocusProp?: (
        layout: FocusableComponentLayout,
        props: object,
        details: FocusDetails
    ) => void
}

export const SeriesEpisodeSliderItem: React.FC<
    SeriesEpisodeSliderItemProps
> = ({
    episode,
    handleMouseEnter,
    handleBuyEpisode,
    progressBarWrapper,
    notPurchased,
    buyButtonStyle,
    onFocusProp,
}) => {
    const { ref, focused } = useFocusable({
        onEnterPress: () => handleBuyEpisode(episode),
        onFocus: onFocusProp,
    })

    const hoverImageStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: 0,
        left: 0,
        height: '96%',
        width: '100%',
        borderRadius: '8px',
        transition: 'opacity 0.3s ease-in-out, backdropFilter 0.4s ease-in-out',
        opacity: 0,
        '&:hover': {
            backdropFilter: 'blur(5px)',
            opacity: 1,
        },
    }

    return (
        <Box
            ref={ref}
            onMouseEnter={() => handleMouseEnter && handleMouseEnter(episode)}
            sx={{
                cursor: 'pointer',
                outline: focused && isSmartTV() ? '2px solid blue' : 'none',
                borderRadius: '8px',
                marginRight: '8px',
                overflow: 'hidden',
            }}>
            <Box
                sx={{
                    position: 'relative',
                }}>
                <LazyLoadImage
                    style={{ borderRadius: '8px' }}
                    alt={episode.title || 'episode cover image'}
                    src={episode.imageUrl}
                    height={episodeItemHeight}
                    width={
                        episode.aspectRatio
                            ? episodeItemHeight *
                              parseAspectRatioToNumber(episode.aspectRatio)
                            : episodeItemHeight
                    }
                    placeholderSrc={placeholder}
                    onError={(event) => {
                        const target = event.target as HTMLImageElement
                        target.onerror = null
                        target.src = placeholder
                    }}
                />
                {episode.position && (
                    <Box sx={progressBarWrapper}>
                        <ProgressBar
                            value={episode.position}
                            min={1}
                            max={100}
                            width={100}
                        />
                    </Box>
                )}
                <Box sx={hoverImageStyle}>
                    <PlayIcon />
                </Box>
            </Box>

            {episode.isPurchased === 1 && (
                <>
                    <Box sx={notPurchased}></Box>
                    <Button
                        sx={buyButtonStyle}
                        onClick={() => handleBuyEpisode(episode)}>
                        Buy
                        <Box sx={iconStyle}>
                            <BuyButton />
                        </Box>
                    </Button>
                </>
            )}
            <Typography variant="label2">{episode.title}</Typography>
        </Box>
    )
}
